import {
  legacyEnglishCatalog,
  legacyEnglishPatterns,
} from '../legacyEnglishCatalog.js';
import {
  legacyEnglishFleet,
  legacyEnglishFleetPatterns,
} from './legacyEnglishFleet.js';
import {
  legacyEnglishOperations,
  legacyEnglishOperationsPatterns,
} from './legacyEnglishOperations.js';
import {
  legacyEnglishTransport,
  legacyEnglishTransportPatterns,
} from './legacyEnglishTransport.js';
import {
  legacyEnglishSystem,
  legacyEnglishSystemPatterns,
} from './legacyEnglishSystem.js';
import {
  legacyEnglishRemaining,
  legacyEnglishRemainingPatterns,
} from './legacyEnglishRemaining.js';

const englishMessages = Object.freeze({
  ...legacyEnglishCatalog,
  ...legacyEnglishFleet,
  ...legacyEnglishOperations,
  ...legacyEnglishTransport,
  ...legacyEnglishSystem,
  ...legacyEnglishRemaining,
});

const englishPatterns = Object.freeze([
  // Keep every imported pack visible to the static coverage verifier. Runtime
  // precedence is calculated globally below because no fixed pack order can
  // put every feature rule before every broad compatibility fallback.
  ...legacyEnglishRemainingPatterns,
  ...legacyEnglishSystemPatterns,
  ...legacyEnglishTransportPatterns,
  ...legacyEnglishOperationsPatterns,
  ...legacyEnglishFleetPatterns,
  ...legacyEnglishPatterns,
]);

const GENERIC_CAPTURE_PATTERN = /\((?:\.\*|\.\+|\[\\s\\S\]\*|\[\\s\\S\]\+)\??\)/gu;

const getPatternSpecificity = ([pattern]) => {
  const source = pattern.source;
  const genericCaptures = source.match(GENERIC_CAPTURE_PATTERN)?.length || 0;
  const fixedStructureLength = source.replace(GENERIC_CAPTURE_PATTERN, '').length;

  return { fixedStructureLength, genericCaptures, sourceLength: source.length };
};

const orderedEnglishPatterns = Object.freeze([...englishPatterns].sort((left, right) => {
  const leftSpecificity = getPatternSpecificity(left);
  const rightSpecificity = getPatternSpecificity(right);

  return rightSpecificity.fixedStructureLength - leftSpecificity.fixedStructureLength
    || leftSpecificity.genericCaptures - rightSpecificity.genericCaptures
    || rightSpecificity.sourceLength - leftSpecificity.sourceLength;
}));

// Transitional packs for screens that have not yet moved to semantic keys.
// Adding a locale requires an explicit, reviewed UI pack; an API-advertised
// language is never exposed by the frontend manifest without one.
export const legacyLocalePacks = Object.freeze({
  en: Object.freeze({
    messages: englishMessages,
    patterns: orderedEnglishPatterns,
  }),
});

export const hasLegacyLocalePack = (language) => {
  const languageCode = String(language || '').trim().toLowerCase().split(/[-_]/)[0];
  return Boolean(legacyLocalePacks[languageCode]);
};

const normalizeWhitespace = (value) => value.trim().replace(/\s+/g, ' ');

export const translateLegacyText = (language, value) => {
  if (typeof value !== 'string' || !value.trim()) return value;
  const languageCode = String(language || '').trim().toLowerCase().split(/[-_]/)[0];
  const pack = legacyLocalePacks[languageCode];
  if (!pack) return value;

  const leading = value.match(/^\s*/)?.[0] || '';
  const trailing = value.match(/\s*$/)?.[0] || '';
  const source = normalizeWhitespace(value);
  const exact = pack.messages[source];
  if (exact) return `${leading}${exact}${trailing}`;

  for (const [pattern, replacement] of pack.patterns) {
    if (pattern.test(source)) {
      return `${leading}${source.replace(pattern, replacement)}${trailing}`;
    }
  }

  return value;
};
